import type { TreatmentPlanDto } from '../api/generated';

export interface RenamedTreatmentPlan {
  from: string;
  to: string;
  count: number;
}

// A rename is neither an add nor a delete, so the save review needs it stated
// separately or it shows a dirty plan it cannot explain. Plans renamed the same
// way collapse into one entry with a count, matching how adds and deletes read.
export function collectRenamedTreatmentPlans(
  currentPlans: TreatmentPlanDto[],
  serverPlans: TreatmentPlanDto[]
): RenamedTreatmentPlan[] {
  const serverNamesById = new Map(serverPlans.map((p) => [p.id, p.name]));
  const renamedByPair = new Map<string, RenamedTreatmentPlan>();

  for (const plan of currentPlans) {
    const previousName = serverNamesById.get(plan.id);
    if (previousName === undefined || previousName === plan.name) continue;

    // Encoded rather than concatenated: a name may legitimately contain any
    // separator we could pick, and a collision would silently merge two
    // different renames into one miscounted line.
    const key = JSON.stringify([previousName, plan.name]);
    const existing = renamedByPair.get(key);
    if (existing) existing.count += 1;
    else renamedByPair.set(key, { from: previousName, to: plan.name, count: 1 });
  }

  return [...renamedByPair.values()];
}

// A reorder adds and deletes nothing and renames nothing, so the save review
// would otherwise show a dirty plan it cannot explain. Only plans present in
// both arrays are compared, so an add or a delete on its own never reads as a
// reorder — it already has its own line.
export function hasTreatmentPlanOrderChanged(
  currentPlans: TreatmentPlanDto[],
  serverPlans: TreatmentPlanDto[]
): boolean {
  const serverIds = new Set(serverPlans.map((p) => p.id));
  const currentIds = new Set(currentPlans.map((p) => p.id));

  const currentSequence = currentPlans.filter((p) => serverIds.has(p.id)).map((p) => p.id);
  const serverSequence = serverPlans.filter((p) => currentIds.has(p.id)).map((p) => p.id);

  return currentSequence.some((id, i) => id !== serverSequence[i]);
}
